import { useEffect, useRef, useState } from "react";
import Badge from "@/components/base/Badge";
import { useSession } from "@/auth/session";
import {
  createManualChargePayment,
  createManualPayment,
  getPaymentDebtSummary,
  listContracts,
  listExpenseConcepts,
  listPaymentMethods,
  searchPaymentDebts,
  searchPayments,
  type ContractListItem,
  type ExpenseConceptItem,
  type PaymentDebtSearchItem,
  type PaymentDebtSummary,
  type PaymentMethodItem,
  type PaymentReceiptItem,
} from "@/lib/api";
import {
  buildReceiptHtml,
  downloadReceiptHtml,
  formatDocumentLabel,
  openPrintWindow,
} from "@/lib/receipt";
import CashClosureModal from "./components/CashClosureModal";
import PaymentVoidModal from "./components/PaymentVoidModal";

type PageMode = "pago" | "manual" | "reimprimir";
type Step = "buscar" | "seleccionar" | "pagar" | "exito";
type ManualStep = "form" | "exito";

type SelectedInvoice = {
  id: string;
  folio: string | null;
  documentType: string;
  status: string;
  dueDate: string;
  totalAmount: number;
  paidAmount: number;
  outstandingAmount: number;
};

type ReceiptResult = {
  payment: {
    id: string;
    receiptNumber: string;
    amount: number;
    paidAt: string;
    reference: string | null;
  };
  allocations: Array<{ billingDocumentId: string; allocatedAmount: number }>;
  receipt?: {
    id: string;
    payloadJson?: {
      allocations?: Array<{ billingDocumentId: string; allocatedAmount: number }>;
    };
  };
};

type ManualChargeFormItem = {
  expenseConceptId: string;
  amount: string;
};

type BackupImageState = {
  dataUrl: string;
  fileName: string;
};

function roundPayableTotal(amount: number): number {
  const units = Math.abs(amount) % 10;
  // Regla del Banco Central: 1 a 5 hacia abajo, 6 a 9 hacia arriba.
  // https://www.billetesymonedas.cl/Monedas/ReglaRedondeo
  // Tiene que coincidir con roundPaymentTotal de apps/api/src/common/money.ts.
  return units <= 5 ? amount - units : amount + (10 - units);
}

/**
 * El redondeo a la decena solo corre en efectivo, porque solo ahi hay vuelto.
 *
 * Tiene que coincidir con isCashPaymentMethod de apps/api/src/common/money.ts:
 * si aca se redondea y alla no, el monto enviado supera la deuda y la API
 * rechaza el pago.
 */
function esPagoEnEfectivo(code?: string): boolean {
  const normalizado = code?.trim().toLowerCase();
  return normalizado === "manual" || normalizado === "efectivo" || normalizado === "cash";
}

type ManualChargeResult = {
  payment: {
    id: string;
    receiptNumber: string;
    amount: number;
    paidAt: string;
    reference: string | null;
  };
  allocations: Array<{ billingDocumentId: string; allocatedAmount: number }>;
  receipt: {
    id: string;
    payloadJson?: {
      allocations?: Array<{ billingDocumentId: string; allocatedAmount: number }>;
    };
  };
  billingDocument: {
    id: string;
    folio: string | null;
    documentType: "33" | "34" | "39" | "41";
    totalAmount: number;
    status: string;
  } | null;
};


function dueDateDay(d: string): number {
  const date = new Date(d);
  return Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate());
}

function emptyBackupImage(): BackupImageState {
  return { dataUrl: "", fileName: "" };
}

function readFileAsDataUrl(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(typeof reader.result === "string" ? reader.result : "");
    reader.onerror = () => reject(new Error("No se pudo leer la imagen de respaldo."));
    reader.readAsDataURL(file);
  });
}

export default function RegistrarPagoPage() {
  const { session, profile } = useSession();
  const [mode, setMode] = useState<PageMode>("pago");
  const [cashClosureOpen, setCashClosureOpen] = useState(false);
  const [paymentVoidOpen, setPaymentVoidOpen] = useState(false);

  // ─── PAGO ───
  const [step, setStep] = useState<Step>("buscar");
  const [query, setQuery] = useState("");
  const [searchType, setSearchType] = useState<"rut" | "contrato">("rut");
  const [loadingSearch, setLoadingSearch] = useState(false);
  const [searchError, setSearchError] = useState("");
  const [resultados, setResultados] = useState<PaymentDebtSearchItem[]>([]);
  const inputRef = useRef<HTMLInputElement>(null);

  const [selectedDebt, setSelectedDebt] = useState<PaymentDebtSearchItem | null>(null);
  const [lastPayment, setLastPayment] = useState<PaymentDebtSummary["lastPayment"]>(null);
  const [boletasPendientes, setBoletasPendientes] = useState<SelectedInvoice[]>([]);
  const [boletasSeleccionadas, setBoletasSeleccionadas] = useState<SelectedInvoice[]>([]);

  const [paymentMethods, setPaymentMethods] = useState<PaymentMethodItem[]>([]);
  const [metodo, setMetodo] = useState("");
  const [montoStr, setMontoStr] = useState("");
  const [referencia, setReferencia] = useState("");
  const [transactionNumber, setTransactionNumber] = useState("");
  const [backupImage, setBackupImage] = useState<BackupImageState>(emptyBackupImage);
  const [procesando, setProcesando] = useState(false);
  const [receiptResult, setReceiptResult] = useState<ReceiptResult | null>(null);
  const [manualStep, setManualStep] = useState<ManualStep>("form");
  const [manualQuery, setManualQuery] = useState("");
  const [manualSearching, setManualSearching] = useState(false);
  const [manualSearchResults, setManualSearchResults] = useState<ContractListItem[]>([]);
  const [manualContract, setManualContract] = useState<ContractListItem | null>(null);
  const [manualConcepts, setManualConcepts] = useState<ExpenseConceptItem[]>([]);
  const [manualItems, setManualItems] = useState<ManualChargeFormItem[]>([{ expenseConceptId: "", amount: "" }]);
  const [manualDocumentType, setManualDocumentType] = useState<"33" | "34" | "39" | "41">("39");
  const [manualMetodo, setManualMetodo] = useState("");
  const [manualReference, setManualReference] = useState("");
  const [manualTransactionNumber, setManualTransactionNumber] = useState("");
  const [manualBackupImage, setManualBackupImage] = useState<BackupImageState>(emptyBackupImage);
  const [manualSubmitting, setManualSubmitting] = useState(false);
  const [manualError, setManualError] = useState("");
  const [manualResult, setManualResult] = useState<ManualChargeResult | null>(null);
  const manualInputRef = useRef<HTMLInputElement>(null);

  // ─── REIMPRIMIR ───
  const [reprQuery, setReprQuery] = useState("");
  const [reprType, setReprType] = useState<"rut" | "contrato">("rut");
  const [reprLoading, setReprLoading] = useState(false);
  const [reprError, setReprError] = useState("");
  const [reprResults, setReprResults] = useState<PaymentReceiptItem[]>([]);
  const reprInputRef = useRef<HTMLInputElement>(null);

  const estadoVariant: Record<string, "success" | "warning" | "error" | "neutral"> = {
    active: "success", partial: "warning", paid: "success",
    external_error: "error", pending_external: "warning", draft: "neutral",
  };

  useEffect(() => {
    if (!session) return;
    void (async () => {
      try {
        const methods = await listPaymentMethods(session);
        const activeMethods = methods.filter((row) => row.status === "active" && row.manualEnabled);
        setPaymentMethods(activeMethods);
        if (activeMethods.length > 0) {
          setMetodo(activeMethods[0].code);
          setManualMetodo(activeMethods[0].code);
        }
      } catch { setPaymentMethods([]); }
    })();
  }, [session]);

  useEffect(() => {
    if (!session) return;
    void (async () => {
      try {
        const rows = await listExpenseConcepts(session);
        setManualConcepts(rows.filter((row) => row.status === "active"));
      } catch {
        setManualConcepts([]);
      }
    })();
  }, [session]);

  const montoNum = parseInt(montoStr.replace(/\D/g, ""), 10) || 0;
  const totalSeleccionado = Math.round(boletasSeleccionadas.reduce((acc, b) => acc + b.outstandingAmount, 0));
  const totalExigible = esPagoEnEfectivo(metodo)
    ? roundPayableTotal(totalSeleccionado)
    : totalSeleccionado;
  const ajusteRedondeo = totalExigible - totalSeleccionado;
  const diferencia = montoNum - totalExigible;
  const selectedPaymentMethod = paymentMethods.find((row) => row.code === metodo) ?? null;
  const selectedManualPaymentMethod = paymentMethods.find((row) => row.code === manualMetodo) ?? null;
  const requiresBackup = selectedPaymentMethod?.requiresBackup ?? false;
  const manualRequiresBackup = selectedManualPaymentMethod?.requiresBackup ?? false;

  useEffect(() => {
    if (requiresBackup) return;
    setTransactionNumber("");
    setBackupImage(emptyBackupImage());
  }, [requiresBackup]);

  useEffect(() => {
    if (manualRequiresBackup) return;
    setManualTransactionNumber("");
    setManualBackupImage(emptyBackupImage());
  }, [manualRequiresBackup]);

  async function handleSearch(): Promise<void> {
    if (!session) return;
    const q = query.trim();
    if (!q) { setResultados([]); return; }
    setLoadingSearch(true);
    setSearchError("");
    try {
      const rows = await searchPaymentDebts(session, q);
      setResultados(rows);
    } catch (error) {
      setSearchError(String(error));
      setResultados([]);
    } finally {
      setLoadingSearch(false);
    }
  }

  async function handleSelectResult(row: PaymentDebtSearchItem): Promise<void> {
    if (!session) return;
    setSelectedDebt(row);
    setBoletasSeleccionadas([]);
    setLoadingSearch(true);
    setSearchError("");
    try {
      const summary = await getPaymentDebtSummary(session, row.contractId);
      setBoletasPendientes(summary.documents.filter((doc) => doc.outstandingAmount > 0));
      setLastPayment(summary.lastPayment ?? null);
      setStep("seleccionar");
    } catch (error) {
      setSearchError(String(error));
    } finally {
      setLoadingSearch(false);
    }
  }

  function handleToggleBoleta(doc: SelectedInvoice, sorted: SelectedInvoice[]): void {
    const currentIds = new Set(boletasSeleccionadas.map((b) => b.id));
    const isSelected = currentIds.has(doc.id);
    const idx = sorted.findIndex((b) => b.id === doc.id);
    if (isSelected) {
      // Deselect this and all newer
      setBoletasSeleccionadas(sorted.slice(0, idx).filter((b) => currentIds.has(b.id)));
    } else {
      // Select from oldest to this (auto-includes all older)
      setBoletasSeleccionadas(sorted.slice(0, idx + 1));
    }
  }

  function handleProcederPago(): void {
    setMontoStr(String(totalExigible));
    setStep("pagar");
  }

  async function handleBackupImageChange(
    file: File | null,
    mode: "regular" | "manual",
  ): Promise<void> {
    if (!file) {
      if (mode === "regular") setBackupImage(emptyBackupImage());
      else setManualBackupImage(emptyBackupImage());
      return;
    }
    if (!file.type.startsWith("image/")) {
      const message = "El respaldo debe ser una imagen.";
      if (mode === "regular") setSearchError(message);
      else setManualError(message);
      return;
    }
    try {
      const dataUrl = await readFileAsDataUrl(file);
      if (mode === "regular") {
        setBackupImage({ dataUrl, fileName: file.name });
        setSearchError("");
      } else {
        setManualBackupImage({ dataUrl, fileName: file.name });
        setManualError("");
      }
    } catch (error) {
      const message = error instanceof Error ? error.message : "No se pudo cargar el respaldo.";
      if (mode === "regular") setSearchError(message);
      else setManualError(message);
    }
  }

  async function handlePagar(): Promise<void> {
    if (!session || !selectedDebt || boletasSeleccionadas.length === 0 || !montoNum || !metodo) return;
    if (montoNum > totalExigible) return;
    if (requiresBackup) {
      if (!transactionNumber.trim()) {
        setSearchError("Debes ingresar el numero de transaccion.");
        return;
      }
      if (!backupImage.dataUrl) {
        setSearchError("Debes subir el respaldo de imagen.");
        return;
      }
    }
    setProcesando(true);
    setSearchError("");
    try {
      const result = await createManualPayment(session, {
        contractId: selectedDebt.contractId,
        paymentMethodCode: metodo,
        documentIds: boletasSeleccionadas.map((b) => b.id),
        amount: montoNum,
        settleFull: montoNum === totalExigible,
        reference: referencia || undefined,
        transactionNumber: transactionNumber || undefined,
        backupImageDataUrl: backupImage.dataUrl || undefined,
      });
      setReceiptResult(result);
      setStep("exito");
    } catch (error) {
      setSearchError(String(error));
    } finally {
      setProcesando(false);
    }
  }

  function buildCurrentReceiptRows() {
    if (!receiptResult || !selectedDebt) return { rows: [], total: 0 };
    const docMap = new Map(boletasPendientes.map((doc) => [doc.id, doc]));
    const allocations = receiptResult.allocations ?? receiptResult.receipt?.payloadJson?.allocations ?? [];
    const rows: Array<{ label: string; dueDate: string | null; amount: number }> = allocations.map((allocation) => {
      const doc = docMap.get(allocation.billingDocumentId);
      if (!doc) return null;
      return { label: formatDocumentLabel(doc.documentType, doc.folio, doc.id), dueDate: doc.dueDate, amount: allocation.allocatedAmount };
    }).filter((r): r is { label: string; dueDate: string; amount: number } => Boolean(r));
    const allocatedTotal = rows.reduce((acc, row) => acc + row.amount, 0);
    const roundingAdjustment = receiptResult.payment.amount - allocatedTotal;
    if (roundingAdjustment !== 0) {
      rows.push({ label: "Ajuste por redondeo", dueDate: null, amount: roundingAdjustment });
    }
    return { rows, total: receiptResult.payment.amount };
  }

  function handlePrintCurrentReceipt(): void {
    if (!receiptResult || !selectedDebt) return;
    const { rows, total } = buildCurrentReceiptRows();
    const html = buildReceiptHtml({
      receiptNumber: receiptResult.payment.receiptNumber,
      paidAt: receiptResult.payment.paidAt,
      reference: receiptResult.payment.reference,
      customerName: selectedDebt.customer.name,
      customerRut: selectedDebt.customer.rut,
      contractNumber: selectedDebt.contractNumber,
      rows, total,
    });
    openPrintWindow(html);
  }

  function handleReset(): void {
    setStep("buscar");
    setQuery("");
    setResultados([]);
    setSelectedDebt(null);
    setLastPayment(null);
    setBoletasPendientes([]);
    setBoletasSeleccionadas([]);
    setMetodo(paymentMethods[0]?.code ?? "");
    setMontoStr("");
    setReferencia("");
    setTransactionNumber("");
    setBackupImage(emptyBackupImage());
    setReceiptResult(null);
    setSearchError("");
    setTimeout(() => inputRef.current?.focus(), 100);
  }

  async function handleManualSearch(): Promise<void> {
    if (!session) return;
    const q = manualQuery.trim();
    if (!q) {
      setManualSearchResults([]);
      return;
    }
    setManualSearching(true);
    setManualError("");
    try {
      const rows = await listContracts(session, { q, status: "active", limit: 20 });
      setManualSearchResults(rows);
    } catch (error) {
      setManualError(String(error));
      setManualSearchResults([]);
    } finally {
      setManualSearching(false);
    }
  }

  function handleSelectManualContract(contract: ContractListItem): void {
    setManualContract(contract);
    setManualSearchResults([]);
    setManualError("");
  }

  function handleManualItemChange(index: number, patch: Partial<ManualChargeFormItem>): void {
    setManualItems((prev) => prev.map((item, itemIndex) => (itemIndex === index ? { ...item, ...patch } : item)));
  }

  function handleManualConceptChange(index: number, expenseConceptId: string): void {
    const concept = manualConcepts.find((item) => item.id === expenseConceptId) ?? null;
    handleManualItemChange(index, {
      expenseConceptId,
      amount: concept?.fixedValue != null ? String(Number(concept.fixedValue)) : "",
    });
  }

  function handleAddManualItem(): void {
    setManualItems((prev) => [...prev, { expenseConceptId: "", amount: "" }]);
  }

  function handleRemoveManualItem(index: number): void {
    setManualItems((prev) => (prev.length === 1 ? prev : prev.filter((_, itemIndex) => itemIndex !== index)));
  }

  function handleResetManual(): void {
    setManualStep("form");
    setManualQuery("");
    setManualSearchResults([]);
    setManualContract(null);
    setManualItems([{ expenseConceptId: "", amount: "" }]);
    setManualDocumentType("39");
    setManualMetodo(paymentMethods[0]?.code ?? "");
    setManualReference("");
    setManualTransactionNumber("");
    setManualBackupImage(emptyBackupImage());
    setManualError("");
    setManualResult(null);
    setTimeout(() => manualInputRef.current?.focus(), 100);
  }

  const manualTotal = manualItems.reduce((sum, item) => {
    const amount = Number(item.amount);
    return sum + (Number.isFinite(amount) ? amount : 0);
  }, 0);

  async function handleSubmitManual(): Promise<void> {
    if (!session || !manualContract || !manualMetodo) return;
    const normalizedItems = manualItems
      .map((item) => ({
        expenseConceptId: item.expenseConceptId,
        amount: Number(item.amount),
      }))
      .filter((item) => item.expenseConceptId && Number.isFinite(item.amount) && item.amount > 0);

    if (normalizedItems.length === 0) {
      setManualError("Debes agregar al menos un concepto con monto valido.");
      return;
    }
    if (manualRequiresBackup) {
      if (!manualTransactionNumber.trim()) {
        setManualError("Debes ingresar el numero de transaccion.");
        return;
      }
      if (!manualBackupImage.dataUrl) {
        setManualError("Debes subir el respaldo de imagen.");
        return;
      }
    }

    setManualSubmitting(true);
    setManualError("");
    try {
      const result = await createManualChargePayment(session, {
        contractId: manualContract.id,
        paymentMethodCode: manualMetodo,
        documentType: manualDocumentType,
        reference: manualReference || undefined,
        transactionNumber: manualTransactionNumber || undefined,
        backupImageDataUrl: manualBackupImage.dataUrl || undefined,
        items: normalizedItems,
      });
      setManualResult(result);
      setManualStep("exito");
    } catch (error) {
      setManualError(String(error));
    } finally {
      setManualSubmitting(false);
    }
  }

  function handlePrintManualReceipt(): void {
    if (!manualResult || !manualContract) return;
    const document = manualResult.billingDocument;
    const rows = document
      ? [{
          label: formatDocumentLabel(document.documentType, document.folio, document.id),
          dueDate: manualResult.payment.paidAt,
          amount: manualResult.payment.amount,
        }]
      : [];
    const html = buildReceiptHtml({
      receiptNumber: manualResult.payment.receiptNumber,
      paidAt: manualResult.payment.paidAt,
      reference: manualResult.payment.reference,
      customerName: manualContract.billingRecipientName || manualContract.customerName || "Cliente",
      customerRut: "",
      contractNumber: manualContract.contractNumber,
      rows,
      total: manualResult.payment.amount,
    });
    openPrintWindow(html);
  }

  // ─── REIMPRIMIR ───
  async function handleReprSearch(): Promise<void> {
    if (!session) return;
    const q = reprQuery.trim();
    if (!q) { setReprResults([]); return; }
    setReprLoading(true);
    setReprError("");
    try {
      const rows = await searchPayments(session, q);
      setReprResults(rows);
      if (rows.length === 0) setReprError("No se encontraron comprobantes.");
    } catch (error) {
      setReprError(String(error));
      setReprResults([]);
    } finally {
      setReprLoading(false);
    }
  }

  function buildReprRows(payment: PaymentReceiptItem) {
    const rows: Array<{ label: string; dueDate: string | null; amount: number }> = payment.allocations.map((a) => ({
      label: formatDocumentLabel(a.billingDocument.documentType, a.billingDocument.folio, a.billingDocumentId),
      dueDate: a.billingDocument.dueDate,
      amount: a.allocatedAmount,
    }));
    const allocatedTotal = rows.reduce((acc, row) => acc + row.amount, 0);
    const roundingAdjustment = payment.amount - allocatedTotal;
    if (roundingAdjustment !== 0) {
      rows.push({ label: "Ajuste por redondeo", dueDate: null, amount: roundingAdjustment });
    }
    return rows;
  }

  function handleReprint(payment: PaymentReceiptItem): void {
    const rows = buildReprRows(payment);
    const total = payment.amount;
    const html = buildReceiptHtml({
      receiptNumber: payment.receiptNumber,
      paidAt: payment.paidAt,
      reference: payment.reference,
      customerName: payment.contract.customer.fullName,
      customerRut: payment.contract.customer.rut,
      contractNumber: payment.contract.contractNumber,
      rows, total,
    });
    openPrintWindow(html);
  }

  function handleDownload(payment: PaymentReceiptItem): void {
    const rows = buildReprRows(payment);
    const total = payment.amount;
    const html = buildReceiptHtml({
      receiptNumber: payment.receiptNumber,
      paidAt: payment.paidAt,
      reference: payment.reference,
      customerName: payment.contract.customer.fullName,
      customerRut: payment.contract.customer.rut,
      contractNumber: payment.contract.contractNumber,
      rows, total,
    });
    downloadReceiptHtml(html, payment.receiptNumber);
  }

  return (
    <>
    <div className="max-w-3xl mx-auto space-y-5">
      <div>
        <h1 className="text-xl font-bold text-gray-800">Pagos</h1>
      </div>

      {/* Tabs de modo */}
      <div className="flex gap-1 bg-gray-100 rounded-xl p-1 w-fit">
        <button
          onClick={() => setMode("pago")}
          className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors ${mode === "pago" ? "bg-white text-teal-700 shadow-sm" : "text-gray-500 hover:text-gray-700"}`}
        >
          <i className="ri-bill-line mr-1.5" />Pago de documento
        </button>
        <button
          onClick={() => { setMode("manual"); setTimeout(() => manualInputRef.current?.focus(), 100); }}
          className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors ${mode === "manual" ? "bg-white text-teal-700 shadow-sm" : "text-gray-500 hover:text-gray-700"}`}
        >
          <i className="ri-cash-line mr-1.5" />Pago manual
        </button>
        <button
          onClick={() => { setMode("reimprimir"); setTimeout(() => reprInputRef.current?.focus(), 100); }}
          className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors ${mode === "reimprimir" ? "bg-white text-teal-700 shadow-sm" : "text-gray-500 hover:text-gray-700"}`}
        >
          <i className="ri-printer-line mr-1.5" />Reimprimir comprobante
        </button>
      </div>

      {searchError ? <div className="p-3 rounded-lg bg-red-50 border border-red-200 text-sm text-red-700">{searchError}</div> : null}

      {/* ═══ MODO PAGO ═══ */}
      {mode === "pago" && (
        <>
          {step === "buscar" && (
            <div className="bg-white rounded-xl border border-gray-100 p-6 space-y-5">
              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-2">Buscar por</label>
                <div className="flex gap-2">
                  {[
                    { value: "rut", label: "RUT", icon: "ri-id-card-line" },
                    { value: "contrato", label: "N° Contrato", icon: "ri-file-paper-2-line" },
                  ].map((t) => (
                    <button
                      key={t.value}
                      onClick={() => { setSearchType(t.value as typeof searchType); setResultados([]); setQuery(""); }}
                      className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium ${searchType === t.value ? "bg-teal-600 text-white" : "bg-gray-100 text-gray-600"}`}
                    >
                      <i className={t.icon} />{t.label}
                    </button>
                  ))}
                </div>
              </div>

              <div className="flex gap-2">
                <input
                  ref={inputRef}
                  autoFocus
                  type="text"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter") void handleSearch(); }}
                  placeholder={searchType === "rut" ? "Ej: 12.345.678-9" : "Ej: 40570"}
                  className="w-full px-4 py-3 text-sm border border-gray-200 rounded-xl focus:outline-none focus:border-teal-400"
                />
                <button
                  onClick={() => void handleSearch()}
                  disabled={loadingSearch}
                  className="px-4 py-3 text-sm font-medium text-white bg-teal-600 rounded-xl hover:bg-teal-700 disabled:opacity-60"
                >
                  {loadingSearch ? "Buscando..." : "Buscar"}
                </button>
              </div>

              <div className="space-y-2">
                {resultados.map((row) => (
                  <button
                    key={row.contractId}
                    onClick={() => void handleSelectResult(row)}
                    className="w-full text-left p-3 rounded-xl border border-gray-100 hover:border-teal-200 hover:bg-teal-50/30"
                  >
                    <div className="flex items-center justify-between gap-2">
                      <div>
                        <div className="text-sm font-semibold text-gray-800">{row.customer.name}</div>
                        <div className="text-xs text-gray-500">{row.customer.rut} · Contrato {row.contractNumber}</div>
                        <div className="text-xs text-gray-400">{row.address}</div>
                      </div>
                      <div className="text-right">
                        <div className="text-xs text-gray-400">Deuda</div>
                        <div className="text-sm font-bold text-red-600">${row.debtSummary.outstandingAmount.toLocaleString("es-CL")}</div>
                      </div>
                    </div>
                  </button>
                ))}
                {!loadingSearch && query.trim() && resultados.length === 0 ? (
                  <p className="text-sm text-gray-500 text-center py-4">Sin resultados</p>
                ) : null}
              </div>
            </div>
          )}

          {step === "seleccionar" && selectedDebt && (
            <div className="space-y-4">
              <div className="bg-white rounded-xl border border-gray-100 p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <div className="font-semibold text-gray-800">{selectedDebt.customer.name}</div>
                    <div className="text-xs text-gray-500">{selectedDebt.customer.rut} · Contrato {selectedDebt.contractNumber}</div>
                  </div>
                  <button onClick={handleReset} className="text-xs text-teal-600">Cambiar</button>
                </div>

                {lastPayment ? (
                  <div className="mt-3 pt-3 border-t border-gray-100 flex items-center gap-3">
                    <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-emerald-50 shrink-0">
                      <i className="ri-history-line text-emerald-600 text-sm" />
                    </div>
                    <div className="min-w-0">
                      <div className="text-xs text-gray-400">Último pago</div>
                      <div className="text-sm font-semibold text-gray-800">
                        ${lastPayment.amount.toLocaleString("es-CL")}
                        <span className="text-xs font-normal text-gray-500 ml-2">
                          {new Date(lastPayment.paidAt).toLocaleDateString("es-CL")}
                        </span>
                      </div>
                      {(lastPayment.receiptNumber || lastPayment.paymentMethodName) && (
                        <div className="text-xs text-gray-400">
                          {lastPayment.receiptNumber ? `N° ${lastPayment.receiptNumber}` : ""}
                          {lastPayment.receiptNumber && lastPayment.paymentMethodName ? " · " : ""}
                          {lastPayment.paymentMethodName ?? ""}
                        </div>
                      )}
                    </div>
                  </div>
                ) : (
                  <div className="mt-3 pt-3 border-t border-gray-100 text-xs text-gray-400">
                    Sin pagos registrados
                  </div>
                )}
              </div>
              <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
                <div className="px-5 py-4 border-b border-gray-100">
                  <h3 className="text-sm font-semibold text-gray-700">Boletas pendientes</h3>
                </div>
                {boletasPendientes.length === 0 ? (
                  <div className="text-center py-8 text-sm text-gray-500">No hay boletas pendientes para este contrato.</div>
                ) : (
                  <div className="divide-y divide-gray-50">
                    {[...boletasPendientes]
                      .sort((a, b) => dueDateDay(a.dueDate) - dueDateDay(b.dueDate))
                      .map((b, _idx, sorted) => {
                        const isSelected = boletasSeleccionadas.some((s) => s.id === b.id);
                        return (
                          <button
                            key={b.id}
                            onClick={() => handleToggleBoleta(b, sorted)}
                            className={`w-full flex items-center gap-3 px-5 py-4 text-left transition-colors ${isSelected ? "bg-teal-50/60" : "hover:bg-teal-50/40"}`}
                          >
                            <div className={`w-5 h-5 rounded border-2 flex items-center justify-center flex-shrink-0 transition-colors ${isSelected ? "bg-teal-600 border-teal-600" : "border-gray-300 bg-white"}`}>
                              {isSelected && <i className="ri-check-line text-white text-xs" />}
                            </div>
                            <div className="flex-1 flex items-center justify-between min-w-0">
                              <div>
                                <div className="text-sm font-semibold text-gray-800">
                                  {formatDocumentLabel(b.documentType, b.folio, b.id)}
                                </div>
                                <div className="text-xs text-gray-500">Vence: {new Date(b.dueDate).toLocaleDateString("es-CL")}</div>
                              </div>
                              <div className="text-right ml-3 flex-shrink-0">
                                <Badge variant={estadoVariant[b.status] ?? "neutral"} size="sm">{b.status}</Badge>
                                {b.status === "partial" && b.paidAmount > 0 ? (
                                  <div className="mt-1 space-y-0.5">
                                    <div className="text-xs text-gray-400 line-through">
                                      ${Math.round(b.totalAmount).toLocaleString("es-CL")}
                                    </div>
                                    <div className="text-xs text-green-600 font-medium">
                                      −${Math.round(b.paidAmount).toLocaleString("es-CL")} abonado
                                    </div>
                                    <div className="text-sm font-bold text-gray-800">
                                      ${Math.round(b.outstandingAmount).toLocaleString("es-CL")}
                                    </div>
                                  </div>
                                ) : (
                                  <div className="text-sm font-bold text-gray-800 mt-1">
                                    ${Math.round(b.outstandingAmount).toLocaleString("es-CL")}
                                  </div>
                                )}
                              </div>
                            </div>
                          </button>
                        );
                      })}
                    {boletasSeleccionadas.length > 0 && (
                      <div className="px-5 py-4 bg-teal-50/40 flex items-center justify-between">
                        <div>
                          <div className="text-xs text-gray-500">
                            {boletasSeleccionadas.length} boleta{boletasSeleccionadas.length > 1 ? "s" : ""} seleccionada{boletasSeleccionadas.length > 1 ? "s" : ""}
                          </div>
                          <div className="text-base font-bold text-teal-700">${totalSeleccionado.toLocaleString("es-CL")}</div>
                        </div>
                        <button
                          onClick={handleProcederPago}
                          className="px-5 py-2.5 text-sm font-medium text-white bg-teal-600 rounded-xl hover:bg-teal-700"
                        >
                          Proceder al pago
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          )}

          {step === "pagar" && boletasSeleccionadas.length > 0 && selectedDebt && (
            <div className="space-y-4">
              <div className="bg-white rounded-xl border border-gray-100 p-5">
                <div className="text-xs text-gray-400 mb-3">
                  {boletasSeleccionadas.length === 1 ? "Boleta a pagar" : `${boletasSeleccionadas.length} boletas a pagar`}
                </div>
                <div className="space-y-2">
                  {boletasSeleccionadas.map((b) => (
                    <div key={b.id} className="flex items-center justify-between">
                      <div>
                        <div className="text-sm font-semibold text-gray-800">
                          {formatDocumentLabel(b.documentType, b.folio, b.id)}
                        </div>
                        <div className="text-xs text-gray-500">Vence: {new Date(b.dueDate).toLocaleDateString("es-CL")}</div>
                      </div>
                      <div className="text-sm font-bold text-gray-800">${b.outstandingAmount.toLocaleString("es-CL")}</div>
                    </div>
                  ))}
                </div>
                <div className="space-y-1.5 pt-3 mt-3 border-t border-gray-100">
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-600">Saldo seleccionado</span>
                    <span className="text-sm font-semibold text-gray-700">${totalSeleccionado.toLocaleString("es-CL")}</span>
                  </div>
                  {ajusteRedondeo !== 0 && (
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-600">Ajuste a decena</span>
                      <span className="text-sm font-semibold text-gray-700">
                        {ajusteRedondeo > 0 ? "+" : "-"}${Math.abs(ajusteRedondeo).toLocaleString("es-CL")}
                      </span>
                    </div>
                  )}
                  <div className="flex items-center justify-between pt-1">
                    <span className="text-sm font-semibold text-gray-700">Total a pagar</span>
                    <span className="text-base font-bold text-teal-700">${totalExigible.toLocaleString("es-CL")}</span>
                  </div>
                </div>
              </div>
              <div className="bg-white rounded-xl border border-gray-100 p-5 space-y-4">
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-2">Método de pago</label>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    {paymentMethods.map((m) => (
                      <button
                        key={m.code}
                        onClick={() => {
                          setMetodo(m.code);
                          // totalExigible todavia trae el valor del metodo anterior,
                          // asi que se recalcula con el que se acaba de elegir.
                          setMontoStr(String(
                            esPagoEnEfectivo(m.code)
                              ? roundPayableTotal(totalSeleccionado)
                              : totalSeleccionado,
                          ));
                        }}
                        className={`flex items-center gap-2 px-3 py-2 rounded-lg border text-sm ${metodo === m.code ? "border-teal-500 bg-teal-50 text-teal-700" : "border-gray-200 text-gray-600"}`}
                      >
                        <i className="ri-bank-card-line" />{m.name}
                      </button>
                    ))}
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-semibold text-gray-600 mb-1.5">Monto recibido</label>
                    <input
                      type="text"
                      value={montoNum > 0 ? montoNum.toLocaleString("es-CL") : ""}
                      onChange={(e) => setMontoStr(e.target.value.replace(/\D/g, ""))}
                      className="w-full px-3 py-2.5 text-sm border border-gray-200 rounded-xl"
                    />
                    {montoNum > 0 && diferencia !== 0 ? (
                      <p className={`text-xs mt-1 ${diferencia > 0 ? "text-red-500" : "text-gray-500"}`}>
                        {diferencia > 0 ? "El monto no puede superar el saldo pendiente" : `Diferencia: $${Math.abs(diferencia).toLocaleString("es-CL")}`}
                      </p>
                    ) : null}
                  </div>
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1.5">Referencia (opcional)</label>
                  <input
                    type="text"
                    value={referencia}
                    onChange={(e) => setReferencia(e.target.value)}
                    className="w-full px-3 py-2.5 text-sm border border-gray-200 rounded-xl"
                  />
                </div>
                {requiresBackup && (
                  <>
                    <div>
                      <label className="block text-xs font-semibold text-gray-600 mb-1.5">Nro de transaccion</label>
                      <input
                        type="text"
                        value={transactionNumber}
                        onChange={(e) => setTransactionNumber(e.target.value)}
                        className="w-full px-3 py-2.5 text-sm border border-gray-200 rounded-xl"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-gray-600 mb-1.5">Respaldo de imagen</label>
                      <input
                        type="file"
                        accept="image/*"
                        onChange={(e) => void handleBackupImageChange(e.target.files?.[0] ?? null, "regular")}
                        className="block w-full text-sm text-gray-600 file:mr-3 file:rounded-lg file:border-0 file:bg-teal-50 file:px-3 file:py-2 file:text-sm file:font-medium file:text-teal-700"
                      />
                      {backupImage.fileName ? (
                        <p className="mt-1 text-xs text-gray-500">Archivo: {backupImage.fileName}</p>
                      ) : null}
                    </div>
                  </>
                )}
                <div className="flex items-center justify-between">
                  <button onClick={() => setStep("seleccionar")} className="px-4 py-2 text-sm text-gray-600 bg-gray-100 rounded-xl">
                    Volver
                  </button>
                  <button
                    onClick={() => void handlePagar()}
                    disabled={procesando || montoNum <= 0 || montoNum > totalExigible || !metodo}
                    className="px-6 py-2.5 text-sm font-medium text-white bg-teal-600 rounded-xl disabled:opacity-60"
                  >
                    {procesando ? "Procesando..." : "Confirmar pago"}
                  </button>
                </div>
              </div>
            </div>
          )}

          {step === "exito" && boletasSeleccionadas.length > 0 && selectedDebt && (
            <div className="bg-white rounded-xl border border-gray-100 p-8 text-center">
              <div className="w-16 h-16 flex items-center justify-center rounded-full bg-emerald-100 mx-auto mb-4">
                <i className="ri-checkbox-circle-line text-emerald-600 text-3xl" />
              </div>
              <h2 className="text-lg font-bold text-gray-800 mb-1">Pago registrado</h2>
              <p className="text-sm text-gray-500 mb-4">
                {receiptResult ? `Comprobante ${receiptResult.payment.receiptNumber}.` : ""}
              </p>
              <div className="flex items-center justify-center gap-2">
                <button onClick={handlePrintCurrentReceipt} className="px-5 py-2.5 text-sm font-medium text-white bg-teal-600 rounded-xl">
                  <i className="ri-printer-line mr-1.5" />Imprimir comprobante
                </button>
                <button onClick={handleReset} className="px-5 py-2.5 text-sm font-medium text-teal-700 border border-teal-200 bg-teal-50 rounded-xl">
                  Registrar otro pago
                </button>
              </div>
            </div>
          )}
        </>
      )}

      {/* ═══ MODO REIMPRIMIR ═══ */}
      {mode === "manual" && (
        <div className="space-y-4">
          {manualError ? <div className="p-3 rounded-lg bg-red-50 border border-red-200 text-sm text-red-700">{manualError}</div> : null}

          {manualStep === "form" && (
            <>
              <div className="bg-white rounded-xl border border-gray-100 p-6 space-y-5">
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-2">Buscar contrato</label>
                  <div className="flex gap-2">
                    <input
                      ref={manualInputRef}
                      type="text"
                      value={manualQuery}
                      onChange={(e) => setManualQuery(e.target.value)}
                      onKeyDown={(e) => { if (e.key === "Enter") void handleManualSearch(); }}
                      placeholder="Contrato, titular o direccion"
                      className="w-full px-4 py-3 text-sm border border-gray-200 rounded-xl focus:outline-none focus:border-teal-400"
                    />
                    <button
                      onClick={() => void handleManualSearch()}
                      disabled={manualSearching}
                      className="px-4 py-3 text-sm font-medium text-white bg-teal-600 rounded-xl hover:bg-teal-700 disabled:opacity-60"
                    >
                      {manualSearching ? "Buscando..." : "Buscar"}
                    </button>
                  </div>
                </div>

                {manualSearchResults.length > 0 && (
                  <div className="space-y-2">
                    {manualSearchResults.map((contract) => (
                      <button
                        key={contract.id}
                        onClick={() => handleSelectManualContract(contract)}
                        className="w-full text-left p-3 rounded-xl border border-gray-100 hover:border-teal-200 hover:bg-teal-50/30"
                      >
                        <div className="text-sm font-semibold text-gray-800">
                          {contract.billingRecipientName || contract.customerName || "Cliente sin nombre"}
                        </div>
                        <div className="text-xs text-gray-500">
                          Contrato {contract.contractNumber} · {contract.startAddress}
                        </div>
                        <div className="text-xs text-gray-400">
                          {contract.sector || "Sin sector"} · Tipo actual {contract.billingDocumentType || "-"}
                        </div>
                      </button>
                    ))}
                  </div>
                )}
              </div>

              {manualContract && (
                <div className="bg-white rounded-xl border border-gray-100 p-6 space-y-5">
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <div className="text-sm font-semibold text-gray-800">
                        {manualContract.billingRecipientName || manualContract.customerName || "Cliente"}
                      </div>
                      <div className="text-xs text-gray-500">
                        Contrato {manualContract.contractNumber} · {manualContract.startAddress}
                      </div>
                    </div>
                    <button onClick={() => setManualContract(null)} className="text-xs text-teal-600">Cambiar</button>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs font-semibold text-gray-600 mb-1.5">Tipo de documento</label>
                      <select
                        value={manualDocumentType}
                        onChange={(e) => setManualDocumentType(e.target.value as "33" | "34" | "39" | "41")}
                        className="w-full px-3 py-2.5 text-sm border border-gray-200 rounded-xl"
                      >
                        <option value="39">Boleta afecta</option>
                        <option value="41">Boleta exenta</option>
                        <option value="33">Factura afecta</option>
                        <option value="34">Factura exenta</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-gray-600 mb-1.5">Metodo de pago</label>
                      <select
                        value={manualMetodo}
                        onChange={(e) => setManualMetodo(e.target.value)}
                        className="w-full px-3 py-2.5 text-sm border border-gray-200 rounded-xl"
                      >
                        {paymentMethods.map((method) => (
                          <option key={method.code} value={method.code}>{method.name}</option>
                        ))}
                      </select>
                    </div>
                  </div>

                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <label className="block text-xs font-semibold text-gray-600">Conceptos</label>
                      <button onClick={handleAddManualItem} className="text-xs text-teal-600">Agregar concepto</button>
                    </div>

                    {manualItems.map((item, index) => (
                      <div key={`${index}-${item.expenseConceptId || "nuevo"}`} className="grid grid-cols-1 md:grid-cols-[1fr_180px_44px] gap-3">
                        <select
                          value={item.expenseConceptId}
                          onChange={(e) => handleManualConceptChange(index, e.target.value)}
                          className="w-full px-3 py-2.5 text-sm border border-gray-200 rounded-xl"
                        >
                          <option value="">Selecciona un concepto</option>
                          {manualConcepts.map((concept) => (
                          <option key={concept.id} value={concept.id}>
                              {concept.name} {concept.isBillable ? "(Facturable)" : "(No facturable)"} {concept.isGross ? "- Bruto" : "- Neto"} {concept.generatesDebt ? "- Genera deuda" : "- Cobro inmediato"}
                          </option>
                        ))}
                        </select>
                        <input
                          type="number"
                          min="0"
                          step="0.01"
                          value={item.amount}
                          onChange={(e) => handleManualItemChange(index, { amount: e.target.value })}
                          placeholder="Monto"
                          className="w-full px-3 py-2.5 text-sm border border-gray-200 rounded-xl"
                        />
                        <button
                          onClick={() => handleRemoveManualItem(index)}
                          disabled={manualItems.length === 1}
                          className="h-[42px] w-[42px] rounded-xl border border-gray-200 text-gray-500 disabled:opacity-40"
                        >
                          <i className="ri-delete-bin-line" />
                        </button>
                      </div>
                    ))}
                  </div>

                  <div>
                    <label className="block text-xs font-semibold text-gray-600 mb-1.5">Referencia (opcional)</label>
                    <input
                      type="text"
                      value={manualReference}
                      onChange={(e) => setManualReference(e.target.value)}
                      className="w-full px-3 py-2.5 text-sm border border-gray-200 rounded-xl"
                    />
                  </div>
                  {manualRequiresBackup && (
                    <>
                      <div>
                        <label className="block text-xs font-semibold text-gray-600 mb-1.5">Nro de transaccion</label>
                        <input
                          type="text"
                          value={manualTransactionNumber}
                          onChange={(e) => setManualTransactionNumber(e.target.value)}
                          className="w-full px-3 py-2.5 text-sm border border-gray-200 rounded-xl"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-semibold text-gray-600 mb-1.5">Respaldo de imagen</label>
                        <input
                          type="file"
                          accept="image/*"
                          onChange={(e) => void handleBackupImageChange(e.target.files?.[0] ?? null, "manual")}
                          className="block w-full text-sm text-gray-600 file:mr-3 file:rounded-lg file:border-0 file:bg-teal-50 file:px-3 file:py-2 file:text-sm file:font-medium file:text-teal-700"
                        />
                        {manualBackupImage.fileName ? (
                          <p className="mt-1 text-xs text-gray-500">Archivo: {manualBackupImage.fileName}</p>
                        ) : null}
                      </div>
                    </>
                  )}

                  <div className="flex items-center justify-between rounded-xl bg-slate-50 px-4 py-3">
                    <div>
                      <div className="text-xs text-gray-500">Total a cobrar</div>
                      <div className="text-lg font-bold text-gray-800">${manualTotal.toLocaleString("es-CL")}</div>
                    </div>
                    <button
                      onClick={() => void handleSubmitManual()}
                      disabled={manualSubmitting || !manualContract || !manualMetodo || manualTotal <= 0}
                      className="px-6 py-2.5 text-sm font-medium text-white bg-teal-600 rounded-xl disabled:opacity-60"
                    >
                      {manualSubmitting ? "Procesando..." : "Emitir y registrar pago"}
                    </button>
                  </div>
                </div>
              )}
            </>
          )}

          {manualStep === "exito" && manualResult && manualContract && (
            <div className="bg-white rounded-xl border border-gray-100 p-8 text-center">
              <div className="w-16 h-16 flex items-center justify-center rounded-full bg-emerald-100 mx-auto mb-4">
                <i className="ri-checkbox-circle-line text-emerald-600 text-3xl" />
              </div>
              <h2 className="text-lg font-bold text-gray-800 mb-1">Pago manual registrado</h2>
              <p className="text-sm text-gray-500 mb-2">
                Comprobante {manualResult.payment.receiptNumber}.
              </p>
              {manualResult.billingDocument ? (
                <p className="text-sm text-gray-500 mb-4">
                  Documento {formatDocumentLabel(
                    manualResult.billingDocument.documentType,
                    manualResult.billingDocument.folio,
                    manualResult.billingDocument.id,
                  )}.
                </p>
              ) : null}
              <div className="flex items-center justify-center gap-2">
                <button onClick={handlePrintManualReceipt} className="px-5 py-2.5 text-sm font-medium text-white bg-teal-600 rounded-xl">
                  <i className="ri-printer-line mr-1.5" />Imprimir comprobante
                </button>
                <button onClick={handleResetManual} className="px-5 py-2.5 text-sm font-medium text-teal-700 border border-teal-200 bg-teal-50 rounded-xl">
                  Registrar otro pago manual
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {mode === "reimprimir" && (
        <div className="bg-white rounded-xl border border-gray-100 p-6 space-y-5">
          <div>
            <label className="block text-xs font-semibold text-gray-600 mb-2">Buscar por</label>
            <div className="flex gap-2">
              {[
                { value: "rut", label: "RUT", icon: "ri-id-card-line" },
                { value: "contrato", label: "N° Contrato", icon: "ri-file-paper-2-line" },
              ].map((t) => (
                <button
                  key={t.value}
                  onClick={() => { setReprType(t.value as typeof reprType); setReprResults([]); setReprQuery(""); setReprError(""); }}
                  className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium ${reprType === t.value ? "bg-teal-600 text-white" : "bg-gray-100 text-gray-600"}`}
                >
                  <i className={t.icon} />{t.label}
                </button>
              ))}
            </div>
          </div>

          <div className="flex gap-2">
            <input
              ref={reprInputRef}
              type="text"
              value={reprQuery}
              onChange={(e) => setReprQuery(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") void handleReprSearch(); }}
              placeholder={reprType === "rut" ? "Ej: 12.345.678-9" : "Ej: 40570"}
              className="w-full px-4 py-3 text-sm border border-gray-200 rounded-xl focus:outline-none focus:border-teal-400"
            />
            <button
              onClick={() => void handleReprSearch()}
              disabled={reprLoading}
              className="px-4 py-3 text-sm font-medium text-white bg-teal-600 rounded-xl hover:bg-teal-700 disabled:opacity-60"
            >
              {reprLoading ? "Buscando..." : "Buscar"}
            </button>
          </div>

          {reprError ? <p className="text-sm text-gray-500 text-center">{reprError}</p> : null}

          <div className="space-y-2">
            {reprResults.map((payment) => (
              <div key={payment.id} className="rounded-xl border border-gray-100 p-4">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-sm font-semibold text-gray-800">{payment.contract.customer.fullName}</span>
                      <span className="text-xs text-gray-400">{payment.contract.customer.rut}</span>
                    </div>
                    <div className="text-xs text-gray-500 mt-0.5">
                      Contrato {payment.contract.contractNumber}
                      {" · "}
                      <span className="font-medium text-gray-700">N° {payment.receiptNumber}</span>
                    </div>
                    <div className="text-xs text-gray-400 mt-0.5">
                      {new Date(payment.paidAt).toLocaleString("es-CL")}
                      {payment.paymentMethod ? ` · ${payment.paymentMethod.name}` : ""}
                    </div>
                    {payment.allocations.length > 0 && (
                      <div className="mt-1 text-xs text-gray-400">
                        {payment.allocations.map((a) =>
                          formatDocumentLabel(a.billingDocument.documentType, a.billingDocument.folio, a.billingDocumentId)
                        ).join(", ")}
                      </div>
                    )}
                  </div>
                  <div className="shrink-0 text-right">
                    <div className="text-sm font-bold text-gray-800 mb-2">${payment.amount.toLocaleString("es-CL")}</div>
                    <div className="flex flex-col gap-1">
                      <button
                        onClick={() => handleReprint(payment)}
                        className="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg bg-teal-50 text-teal-700 hover:bg-teal-100"
                      >
                        <i className="ri-printer-line" />Imprimir
                      </button>
                      <button
                        onClick={() => handleDownload(payment)}
                        className="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg bg-gray-50 text-gray-600 hover:bg-gray-100"
                      >
                        <i className="ri-download-line" />Descargar
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
    <div className="fixed bottom-6 right-6 z-40 flex flex-wrap items-center justify-end gap-3">
      <button
        onClick={() => setPaymentVoidOpen(true)}
        className="inline-flex items-center gap-2 rounded-2xl bg-amber-600 px-5 py-3 text-sm font-semibold text-white shadow-lg transition hover:bg-amber-700"
      >
        <i className="ri-close-circle-line text-base" />
        Anular pago
      </button>
      <button
        onClick={() => setCashClosureOpen(true)}
        className="inline-flex items-center gap-2 rounded-2xl bg-slate-900 px-5 py-3 text-sm font-semibold text-white shadow-lg transition hover:bg-slate-800"
      >
        <i className="ri-safe-line text-base" />
        Arqueo de caja
      </button>
    </div>
    <PaymentVoidModal
      open={paymentVoidOpen}
      onClose={() => setPaymentVoidOpen(false)}
      session={session}
    />
    <CashClosureModal
      open={cashClosureOpen}
      onClose={() => setCashClosureOpen(false)}
      session={session}
      currentUserName={profile?.fullName ?? null}
    />
    </>
  );
}
